import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Clock, CheckCircle, XCircle, Layers, Monitor, Smartphone, Volume2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

const GAME_STATES = {
  READY: 'ready',
  DEVICE_SELECT: 'device_select',
  PLAYING: 'playing',
  FINISHED: 'finished',
};

// Device Mode Selection Component
const DeviceModeSelector = ({ onSelect }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-8">
      <div className="text-center mb-6">
        <h2 className="font-heading text-xl font-bold text-[#0B1D51] mb-2">
          Select Your Device
        </h2>
        <p className="text-sm text-[#64748B]">
          Choose how you're taking this test for the best experience
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#0B1D51] hover:bg-[#0B1D51]/5"
          onClick={() => onSelect('desktop')}
          data-testid="select-desktop"
        >
          <Monitor className="w-10 h-10 text-[#0B1D51]" />
          <div>
            <p className="font-semibold">Laptop/Desktop</p>
            <p className="text-xs text-[#64748B]">Keyboard controls</p>
          </div>
        </Button>
        
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#FF9F1C] hover:bg-[#FF9F1C]/5"
          onClick={() => onSelect('mobile')}
          data-testid="select-mobile"
        >
          <Smartphone className="w-10 h-10 text-[#FF9F1C]" />
          <div>
            <p className="font-semibold">Mobile/Tablet</p>
            <p className="text-xs text-[#64748B]">Touch controls</p>
          </div>
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Color Button Component
const ColorButton = ({ colorName, onClick, disabled }) => {
  const colorStyles = {
    red: 'bg-red-500 hover:bg-red-600 border-red-700 shadow-red-500/30',
    green: 'bg-green-500 hover:bg-green-600 border-green-700 shadow-green-500/30',
    blue: 'bg-blue-500 hover:bg-blue-600 border-blue-700 shadow-blue-500/30',
    yellow: 'bg-yellow-400 hover:bg-yellow-500 border-yellow-600 shadow-yellow-500/30',
  };
  
  return (
    <motion.button
      whileTap={{ scale: 0.9 }}
      whileHover={{ scale: 1.05 }}
      onClick={() => onClick(colorName)}
      disabled={disabled}
      className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full ${colorStyles[colorName]} border-b-4 shadow-lg 
        transition-all duration-100 
        ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer active:border-b-2 active:translate-y-0.5'}`}
      data-testid={`color-btn-${colorName}`}
    />
  );
};

// Tone Button Component
const ToneButton = ({ tone, onClick, disabled }) => {
  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      whileHover={{ scale: 1.02 }}
      onClick={() => onClick(tone)}
      disabled={disabled}
      className={`px-6 py-4 sm:px-8 sm:py-5 bg-white border-2 border-slate-300 rounded-lg font-bold text-lg sm:text-xl
        transition-all duration-100 
        ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50 hover:border-slate-400 active:bg-slate-100'}`}
      data-testid={`tone-btn-${tone}`}
    >
      {tone.toUpperCase()}
    </motion.button>
  );
};

// Stimulus Display (center square)
const StimulusDisplay = ({ stimulus }) => {
  if (!stimulus) {
    return (
      <div className="w-24 h-24 sm:w-32 sm:h-32 bg-slate-800 rounded-lg flex items-center justify-center border-2 border-slate-700">
        <span className="text-slate-500 text-xs font-medium">WATCH HERE</span>
      </div>
    );
  }
  
  const colorStyles = {
    red: 'bg-red-500',
    green: 'bg-green-500',
    blue: 'bg-blue-500',
    yellow: 'bg-yellow-400',
  };
  
  return (
    <motion.div
      initial={{ scale: 0, rotate: -10 }}
      animate={{ scale: 1, rotate: 0 }}
      exit={{ scale: 0, rotate: 10 }}
      transition={{ type: "spring", stiffness: 500, damping: 30 }}
      className={`w-24 h-24 sm:w-32 sm:h-32 ${colorStyles[stimulus]} rounded-lg shadow-2xl`}
    />
  );
};

// Audio context for tones
let audioContext = null;

const playTone = (frequency, duration = 400) => {
  try {
    if (!audioContext) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    // Resume audio context if suspended (browser autoplay policy)
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }
    
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration / 1000);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration / 1000);
  } catch (e) {
    console.error('Audio error:', e);
  }
};

// Level settings - 3 mins for level 1, progressively faster stimuli
const LEVEL_SETTINGS = {
  1: { duration: 180, stimulusInterval: [3000, 5000], toneInterval: [4000, 6000], responseTime: 4000 },
  2: { duration: 180, stimulusInterval: [2500, 4000], toneInterval: [3000, 5000], responseTime: 3500 },
  3: { duration: 180, stimulusInterval: [2000, 3500], toneInterval: [2500, 4000], responseTime: 3000 },
  4: { duration: 180, stimulusInterval: [1500, 3000], toneInterval: [2000, 3500], responseTime: 2500 },
  5: { duration: 180, stimulusInterval: [1000, 2500], toneInterval: [1500, 3000], responseTime: 2000 },
};

// Main Multitasking Game Component
const MultitaskingGame = ({ level, onComplete }) => {
  const [gameState, setGameState] = useState(GAME_STATES.READY);
  const [deviceMode, setDeviceMode] = useState(null);
  const [score, setScore] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [missed, setMissed] = useState(0);
  const [currentStimulus, setCurrentStimulus] = useState(null);
  const [currentTone, setCurrentTone] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalStimuli, setTotalStimuli] = useState(0);
  const [isWaitingForColor, setIsWaitingForColor] = useState(false);
  const [isWaitingForTone, setIsWaitingForTone] = useState(false);
  
  const startTimeRef = useRef(Date.now());
  const gameActiveRef = useRef(false);
  const stimulusTimerRef = useRef(null);
  const toneTimerRef = useRef(null);
  const gameTimerRef = useRef(null);
  const colorTimeoutRef = useRef(null);
  const toneTimeoutRef = useRef(null);
  const correctRef = useRef(0);
  const totalRef = useRef(0);
  // Stimuli shown but not yet resolved (answered or auto-missed).
  // Any still-pending stimulus when time runs out must NOT count against the user.
  const pendingRef = useRef(0);
  
  const settings = LEVEL_SETTINGS[level] || LEVEL_SETTINGS[1];
  const colors = ['red', 'green', 'blue', 'yellow'];
  
  // Generate random interval
  const getRandomInterval = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
  
  // Show color stimulus
  const showColorStimulus = useCallback(() => {
    if (!gameActiveRef.current) return;
    
    const color = colors[Math.floor(Math.random() * colors.length)];
    
    setCurrentStimulus(color);
    setIsWaitingForColor(true);
    totalRef.current += 1;
    pendingRef.current += 1;
    setTotalStimuli(totalRef.current);
    
    // Auto-miss if not responded in time
    colorTimeoutRef.current = setTimeout(() => {
      if (gameActiveRef.current) {
        pendingRef.current = Math.max(0, pendingRef.current - 1);
        setMissed(m => m + 1);
        setCurrentStimulus(null);
        setIsWaitingForColor(false);
        setFeedback({ type: 'miss', message: 'Missed color!' });
        setTimeout(() => setFeedback(null), 500);
      }
    }, settings.responseTime);
    
    // Schedule next stimulus
    const nextInterval = getRandomInterval(...settings.stimulusInterval);
    stimulusTimerRef.current = setTimeout(showColorStimulus, nextInterval + settings.responseTime);
  }, [settings]);
  
  // Play tone stimulus
  const playToneStimulus = useCallback(() => {
    if (!gameActiveRef.current) return;
    
    const tone = Math.random() > 0.5 ? 'high' : 'low';
    const frequency = tone === 'high' ? 800 : 300;
    
    playTone(frequency);
    setCurrentTone(tone);
    setIsWaitingForTone(true);
    totalRef.current += 1;
    pendingRef.current += 1;
    setTotalStimuli(totalRef.current);
    
    // Auto-miss if not responded in time
    toneTimeoutRef.current = setTimeout(() => {
      if (gameActiveRef.current) {
        pendingRef.current = Math.max(0, pendingRef.current - 1);
        setMissed(m => m + 1);
        setCurrentTone(null);
        setIsWaitingForTone(false);
        setFeedback({ type: 'miss', message: 'Missed tone!' });
        setTimeout(() => setFeedback(null), 500);
      }
    }, settings.responseTime);
    
    // Schedule next tone
    const nextInterval = getRandomInterval(...settings.toneInterval);
    toneTimerRef.current = setTimeout(playToneStimulus, nextInterval + settings.responseTime);
  }, [settings]);
  
  // Handle color button click
  const handleColorClick = (clickedColor) => {
    if (!isWaitingForColor || !currentStimulus) return;
    
    clearTimeout(colorTimeoutRef.current);
    pendingRef.current = Math.max(0, pendingRef.current - 1);
    
    if (clickedColor === currentStimulus) {
      setScore(s => s + 10);
      setCorrect(c => c + 1);
      correctRef.current += 1;
      setFeedback({ type: 'correct', message: '+10' });
    } else {
      setWrong(w => w + 1);
      setFeedback({ type: 'wrong', message: `Wrong! Was ${currentStimulus}` });
    }
    
    setCurrentStimulus(null);
    setIsWaitingForColor(false);
    setTimeout(() => setFeedback(null), 400);
  };
  
  // Handle tone button click
  const handleToneClick = (clickedTone) => {
    if (!isWaitingForTone || !currentTone) return;
    
    clearTimeout(toneTimeoutRef.current);
    pendingRef.current = Math.max(0, pendingRef.current - 1);
    
    if (clickedTone === currentTone) {
      setScore(s => s + 10);
      setCorrect(c => c + 1);
      correctRef.current += 1;
      setFeedback({ type: 'correct', message: '+10' });
    } else {
      setWrong(w => w + 1);
      setFeedback({ type: 'wrong', message: `Wrong! Was ${currentTone}` });
    }
    
    setCurrentTone(null);
    setIsWaitingForTone(false);
    setTimeout(() => setFeedback(null), 400);
  };
  
  // Finish game
  const finishGame = useCallback(() => {
    if (!gameActiveRef.current) return; // guard against double-invocation
    gameActiveRef.current = false;
    
    clearTimeout(stimulusTimerRef.current);
    clearTimeout(toneTimerRef.current);
    clearTimeout(colorTimeoutRef.current);
    clearTimeout(toneTimeoutRef.current);
    clearInterval(gameTimerRef.current);
    
    setGameState(GAME_STATES.FINISHED);
    setCurrentStimulus(null);
    setCurrentTone(null);
    setIsWaitingForColor(false);
    setIsWaitingForTone(false);
    
    const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
    // Exclude any stimulus that was on-screen but never resolved when the timer ended –
    // the user had no chance to answer it, so it must not reduce their accuracy/score.
    const effectiveTotal = Math.max(correctRef.current, totalRef.current - pendingRef.current);
    const finalScore = correctRef.current * 10; // Use ref for accurate score
    const accuracy = effectiveTotal > 0 ? (correctRef.current / effectiveTotal) * 100 : 0;
    
    onComplete({
      score: finalScore,
      maxScore: effectiveTotal * 10,
      accuracy: accuracy,
      timeSpent: timeSpent
    });
  }, [onComplete]);
  
  // Start game
  const startGame = useCallback(() => {
    // Initialize audio context on user interaction
    if (!audioContext) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    setGameState(GAME_STATES.PLAYING);
    setScore(0);
    setCorrect(0);
    setWrong(0);
    setMissed(0);
    setTotalStimuli(0);
    setCurrentStimulus(null);
    setCurrentTone(null);
    setIsWaitingForColor(false);
    setIsWaitingForTone(false);
    setTimeLeft(settings.duration);
    
    startTimeRef.current = Date.now();
    gameActiveRef.current = true;
    correctRef.current = 0;
    totalRef.current = 0;
    pendingRef.current = 0;
    
    // Start color stimuli after 2 seconds
    stimulusTimerRef.current = setTimeout(showColorStimulus, 2000);
    
    // Start tone stimuli after 3.5 seconds (offset from colors)
    toneTimerRef.current = setTimeout(playToneStimulus, 3500);
    
    // Game countdown timer
    gameTimerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Defer out of the state-updater so onComplete->parent setState
          // never runs during this component's render pass.
          setTimeout(() => finishGame(), 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [settings, showColorStimulus, playToneStimulus, finishGame]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      gameActiveRef.current = false;
      clearTimeout(stimulusTimerRef.current);
      clearTimeout(toneTimerRef.current);
      clearTimeout(colorTimeoutRef.current);
      clearTimeout(toneTimeoutRef.current);
      clearInterval(gameTimerRef.current);
    };
  }, []);
  
  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Device selection screen
  if (gameState === GAME_STATES.READY) {
    return <DeviceModeSelector onSelect={(mode) => { setDeviceMode(mode); setGameState(GAME_STATES.DEVICE_SELECT); }} />;
  }
  
  // Instructions screen
  if (gameState === GAME_STATES.DEVICE_SELECT) {
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-6 sm:pt-8">
          <div className="text-center mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-[#0B1D51]/10 rounded-full flex items-center justify-center">
              <Layers className="w-8 h-8 sm:w-10 sm:h-10 text-[#0B1D51]" />
            </div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0B1D51] mb-2">
              Workload Management
            </h2>
            <p className="text-sm text-[#64748B]">Level {level} - Divided Attention Test</p>
            <p className="text-xs text-[#FF9F1C] font-medium mt-2">
              Develops skills to improve performance on TEA-Occ Workload Management Tests
            </p>
          </div>
          
          <div className="bg-[#F8FAFC] rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <h3 className="font-semibold text-[#0B1D51] mb-3 text-sm sm:text-base">How to Play:</h3>
            <ol className="space-y-2 text-xs sm:text-sm text-[#64748B]">
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">1.</span>
                <span>Watch for <strong>colored squares</strong> appearing in the center</span>
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">2.</span>
                <span>Click the <strong>matching color button</strong> (Red, Green, Blue, or Yellow)</span>
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">3.</span>
                <span>Listen for <strong>audio tones</strong> (High pitch or Low pitch)</span>
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">4.</span>
                <span>Click <strong>HIGH</strong> or <strong>LOW</strong> button to match the tone you hear</span>
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">5.</span>
                <span>Both tasks happen <strong>simultaneously</strong> - stay focused!</span>
              </li>
            </ol>
          </div>
          
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-slate-100 rounded-lg p-3 text-center">
              <Clock className="w-5 h-5 mx-auto mb-1 text-[#0B1D51]" />
              <p className="text-xs text-[#64748B]">Duration</p>
              <p className="font-bold text-[#0B1D51]">{formatTime(settings.duration)}</p>
            </div>
            <div className="bg-slate-100 rounded-lg p-3 text-center">
              <Volume2 className="w-5 h-5 mx-auto mb-1 text-[#FF9F1C]" />
              <p className="text-xs text-[#64748B]">Audio Required</p>
              <p className="font-bold text-[#0B1D51]">Yes</p>
            </div>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 sm:mb-6">
            <p className="text-xs sm:text-sm text-amber-800 flex items-center gap-2">
              <Volume2 className="w-4 h-4 flex-shrink-0" />
              <span><strong>Important:</strong> Turn up your volume to hear the audio tones!</span>
            </p>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 sm:mb-6">
            <p className="text-xs sm:text-sm text-blue-800">
              <strong>RIS-3751-TOM:</strong> This exercise develops skills assessed by TEA-Occ - testing multitasking, divided attention, information processing and controlled response under time pressure.
            </p>
          </div>
          
          <Button 
            onClick={startGame}
            size="lg"
            className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90"
            data-testid="start-game-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Test ({formatTime(settings.duration)})
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  // Playing state
  if (gameState === GAME_STATES.PLAYING) {
    const progress = ((settings.duration - timeLeft) / settings.duration) * 100;
    
    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="text-center">
            <p className="text-xs text-[#64748B] uppercase tracking-wide">Score</p>
            <p className="font-mono text-xl sm:text-2xl font-bold text-[#0B1D51]">{score}</p>
          </div>
          <Badge variant="outline" className="text-[#0B1D51] text-sm sm:text-lg px-3 py-1 font-mono">
            <Clock className="w-4 h-4 mr-1" />
            {formatTime(timeLeft)}
          </Badge>
          <div className="text-center">
            <p className="text-xs text-[#64748B] uppercase tracking-wide">Correct</p>
            <p className="font-mono text-xl sm:text-2xl font-bold text-[#2CB67D]">{correct}</p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-[#FF9F1C]"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        
        {/* Game Area */}
        <Card className="border-slate-200 bg-slate-900">
          <CardContent className="py-6 sm:py-8">
            {/* Color Buttons - Top Row */}
            <div className="flex justify-center gap-4 sm:gap-6 mb-6">
              <ColorButton colorName="red" onClick={handleColorClick} disabled={!isWaitingForColor} />
              <ColorButton colorName="green" onClick={handleColorClick} disabled={!isWaitingForColor} />
            </div>
            
            {/* Center - Stimulus Display and Tone Buttons */}
            <div className="flex justify-center items-center gap-4 sm:gap-8 mb-6">
              <ToneButton tone="low" onClick={handleToneClick} disabled={!isWaitingForTone} />
              
              <AnimatePresence mode="wait">
                <StimulusDisplay stimulus={currentStimulus} key={currentStimulus || 'empty'} />
              </AnimatePresence>
              
              <ToneButton tone="high" onClick={handleToneClick} disabled={!isWaitingForTone} />
            </div>
            
            {/* Color Buttons - Bottom Row */}
            <div className="flex justify-center gap-4 sm:gap-6">
              <ColorButton colorName="yellow" onClick={handleColorClick} disabled={!isWaitingForColor} />
              <ColorButton colorName="blue" onClick={handleColorClick} disabled={!isWaitingForColor} />
            </div>
            
            {/* Audio indicator */}
            {isWaitingForTone && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-4 flex justify-center"
              >
                <Badge className="bg-[#FF9F1C] text-white animate-pulse">
                  <Volume2 className="w-3 h-3 mr-1" /> TONE PLAYING
                </Badge>
              </motion.div>
            )}
          </CardContent>
        </Card>
        
        {/* Feedback */}
        <AnimatePresence>
          {feedback && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`p-3 rounded-lg text-center font-medium text-sm flex items-center justify-center gap-2 ${
                feedback.type === 'correct' 
                  ? 'bg-[#2CB67D] text-white' 
                  : feedback.type === 'miss'
                    ? 'bg-amber-500 text-white'
                    : 'bg-red-500 text-white'
              }`}
            >
              {feedback.type === 'correct' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {feedback.message}
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div className="bg-green-50 rounded p-2">
            <p className="text-green-700 font-medium">Correct: {correct}</p>
          </div>
          <div className="bg-red-50 rounded p-2">
            <p className="text-red-700 font-medium">Wrong: {wrong}</p>
          </div>
          <div className="bg-amber-50 rounded p-2">
            <p className="text-amber-700 font-medium">Missed: {missed}</p>
          </div>
        </div>
      </div>
    );
  }
  
  return null;
};

export default MultitaskingGame;
